import { globals } from 'config/globals';
import HomeColours from 'app/views/home/config/HomeColours';
import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';
import {
	BEHAVIOUR_DEPTH,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PHYSICS,
	FLAG_ACTIVE,
	PHYS_BOUNDARY,
	PHYS_LERPING,
	PHYS_RAIN,
	PHYS_Y_BOUNCE
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { hexToTint } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

/**
 * Physics kernel — verbatim port of ParticlePhysics.tick() (+ its private
 * _processLerp / _processHitTest / _processTarget / _processBoundary /
 * _raindrop and the module-level lerp() ease table). Works in the
 * reference's REL coordinate space (fractions of container width/height)
 * over the relX/relY lanes, hard-writing the px lanes each tick exactly
 * like position.set(pos, true).
 *
 * Reference input -> contract input mapping:
 *   zonePositions.box (y/w/h)      -> boundaryY/boundaryW/boundaryH (x is
 *                                     ALWAYS recomputed as -w/2 — see below)
 *   mouse.particleContainersX.screens -> screensContainerX (the _getRealX
 *                                     seam: physics reads the SCREENS
 *                                     container x regardless of pool —
 *                                     reference quirk, kept)
 *   ParticleTargets.getTarget(id)  -> targetsPx[physTargetSlot - 1], the
 *                                     caller resolves ids once per frame
 *
 * Bug-compatible notes:
 *  - _processBoundary REASSIGNS the boundary to zonePositions.box (x = -w/2)
 *    on entry, so the systemsPArea boundaries written by tick()/target()/
 *    _raindrop() are dead for the actual edge checks — the kernel only ever
 *    checks the box. The `_type` member exists solely to pick which shared
 *    zonePositions object gets its `.x` MUTATED each tick; that shared-object
 *    mutation is a side effect on the timeline's zonePositions bag with no
 *    reader on the home flow and is deliberately NOT replicated (the one
 *    intended deviation — flagged in the stage report).
 *  - the boundary cords come from depth.orginalCords when a depth behaviour
 *    is attached (BEHAVIOUR_DEPTH) — origX/origY lanes — else the rel
 *    position, like _cords().
 *  - _processHitTest's sprite.visible gate is covered by FLAG_ACTIVE (every
 *    home caller pairs them); the cooldown branch decrements and SKIPS the
 *    hit lookup only (not the rest of the tick).
 *  - the bounce colour write is ParticleTint.set = an instant tint write.
 *  - _allowReverseVector is never set true in the reference — dead branch,
 *    not ported.
 *  - tick()'s `if (this._onTargetReached) {}` no-op block is dead; the LIVE
 *    onTargetReached calls (lerp end, target reached) surface through the
 *    onTargetReached callback parameter.
 *  - _inSafeZone is an empty body — not ported.
 *
 * The lerp machine's arming (lerpToPosition / removeLerp / leftDrop / rain /
 * burst...) are ACTIONS — departments write the lanes; only tick-side logic
 * lives here.
 */

/** The module-level lerp() ease table, verbatim (0 = linear passthrough). */
export function physicsLerp(x: number, y: number, a: number, ease: number): number {
	//1 == easeOutCubic
	if (ease == 1) {
		a = 1 - Math.pow(1 - a, 3);
	}

	//2 == easeInOutCubic
	if (ease == 2) {
		a = a < 0.5 ? 4 * a * a * a : 1 - Math.pow(-2 * a + 2, 3) / 2;
	}

	//3 easeInOutBack
	if (ease == 3) {
		const c1 = 1.70158;
		const c2 = c1 * 1.525;

		a =
			a < 0.5
				? (Math.pow(2 * a, 2) * ((c2 + 1) * 2 * a - c2)) / 2
				: (Math.pow(2 * a - 2, 2) * ((c2 + 1) * (a * 2 - 2) + c2) + 2) / 2;
	}

	//4 easeOutBack
	if (ease == 4) {
		const c1 = 1.70158;
		const c3 = c1 + 1;

		a = 1 + c3 * Math.pow(a - 1, 3) + c1 * Math.pow(a - 1, 2);
	}

	return x * (1 - a) + y * a;
}

/** Per-frame scalars for the physics kernel, resolved ONCE by the caller. */
export interface PhysicsFrame {
	deltaTime: number;
	containerW: number;
	containerH: number;
	containerX: number;
	progress: number;
	/** zonePositions.box — x is derived as -w/2 in here, like the reference. */
	boundaryY: number;
	boundaryW: number;
	boundaryH: number;
	/** sceneProps.mouse.particleContainersX.screens (see header). */
	screensContainerX: number;
	/** Frame-resolved ParticleTargets positions ([x, y] px) for the
	 *  physTargetSlot lanes (slot - 1 indexes). Empty when unused. */
	targetsPx: ReadonlyArray<ReadonlyArray<number>>;
	/** ParticlePhysics.onTargetReached — fired on lerp completion and on
	 *  target arrival (vx == vy == 0), with the lane index. */
	onTargetReached: ((lane: number) => void) | null;
}

/** position.set({x, y}, true): rel write + HARD px write (value = target;
 *  the hard path adds NO xOffset — ParticleProgressParallax.set, kept). */
function setHard(core: ParticleCore, i: number, x: number, y: number, frame: PhysicsFrame): void {
	core.relX[i] = x;
	core.relY[i] = y;

	const absX = frame.containerW * x;
	let targetX: number;
	if (core.behaviourMask[i] & BEHAVIOUR_DISABLE_TIMELINE_X) {
		targetX = absX;
	} else {
		//_calculateParallaxDragOffsetX(progress.ratio):
		const drag = (x - 0.5 - (frame.progress - 0.5)) * core.parallaxPower[i] * (frame.containerW * 0.5);
		targetX = absX + frame.containerX + drag;
	}

	core.tx[i] = core.x[i] = targetX;
	core.ty[i] = core.y[i] = y * frame.containerH;
	//ref.position.absX/absY (the pure parallax targets) now read the hard
	//value — repell PUSH consumes them later this same tick:
	core.pTx[i] = core.tx[i];
	core.pTy[i] = core.ty[i];
}

/** _raindrop(resetY = true) — the bottom-of-boundary respawn. */
function raindrop(core: ParticleCore, i: number, frame: PhysicsFrame): void {
	//_type = 'spaces' (all observable branches below are the spaces ones):
	core.vy[i] = 0;
	core.gravity[i] = 0.00002 + Math.random() * 0.0004;

	const y = frame.boundaryY; //resetY branch
	core.vx[i] = 0;

	const boundaryX = -1 * (frame.boundaryW / 2);
	setHard(core, i, boundaryX + Math.random() * frame.boundaryW, y, frame);
}

export function physicsKernel(core: ParticleCore, range: PoolRange, frame: PhysicsFrame): void {
	const mask = core.behaviourMask;
	const flags = core.stateFlags;
	const phys = core.physFlags;
	const dt = frame.deltaTime;
	const hitsActive = ParticleHitTests.active;
	const boundaryX = -1 * (frame.boundaryW / 2); //_processBoundary always rebuilds from box

	for (let i = range.start; i < range.end; i++) {
		if (!(flags[i] & FLAG_ACTIVE) || !(mask[i] & BEHAVIOUR_PHYSICS)) {
			continue;
		}

		//The lerp machine owns the whole tick while lerping:
		if (phys[i] & PHYS_LERPING) {
			core.lerpFrames[i] += dt;

			const timeRatio = Math.min(core.lerpFrames[i] / core.lerpTargetFrames[i], 1);
			const lx = physicsLerp(core.lerpStartX[i], core.lerpEndX[i], timeRatio, core.lerpEase[i]);
			const ly = physicsLerp(core.lerpStartY[i], core.lerpEndY[i], timeRatio, core.lerpEase[i]);

			//position.set({x, y}, false) — SOFT: rel only, the eases carry it:
			core.relX[i] = lx;
			core.relY[i] = ly;

			if (timeRatio >= 1) {
				//removeLerp():
				core.lerpFrames[i] = 0;
				core.lerpTargetFrames[i] = 0;
				phys[i] &= ~PHYS_LERPING;

				if (frame.onTargetReached) {
					frame.onTargetReached(i);
				}
			}
			continue;
		}

		//_processHitTest — cooldown decimation, then the pixel-map readback:
		if (hitsActive) {
			if (core.hitCooldown[i] > 0) {
				core.hitCooldown[i] -= dt;
			} else {
				const realX = frame.screensContainerX + core.x[i];
				const hasHit = ParticleHitTests.hasHit(Math.round(realX), Math.round(core.y[i]));

				if (hasHit) {
					if (hasHit == 1) {
						core.vy[i] *= -1;
						core.vy[i] *= 0.3 + Math.random() * 0.2;
					} else {
						core.vx[i] *= -1;
					}

					if (Math.abs(core.vy[i]) > 0.001) {
						core.tint[i] = hexToTint(HomeColours.getRandomColour(globals.bounceColourTarget));
					}

					core.hitCooldown[i] = 2;
				}
			}
		}

		//(tick's boundary reassignment is a shared-object mutation with no
		//boundary-check effect — see the header; not replicated.)

		if (core.physTargetSlot[i]) {
			//_processTarget — re-resolve the target every tick (they move):
			const target = frame.targetsPx[core.physTargetSlot[i] - 1];

			if (target) {
				const realX = frame.screensContainerX + core.x[i];

				core.vx[i] += core.tgtIncX[i] * dt;
				core.vy[i] += core.tgtIncY[i] * dt;

				if ((core.tgtDirX[i] == 1 && realX >= target[0]) || (core.tgtDirX[i] == -1 && realX <= target[0])) {
					core.vx[i] = 0;
				}

				if ((core.tgtDirY[i] == 1 && core.y[i] >= target[1]) || (core.tgtDirY[i] == -1 && core.y[i] <= target[1])) {
					core.vy[i] = 0;
				}

				if (frame.onTargetReached && core.vx[i] == 0 && core.vy[i] == 0) {
					frame.onTargetReached(i);
				}
			}
		}

		//Integrate (rel space) + the y clamps, then the HARD position write:
		let y = core.relY[i] + core.vy[i] * dt;
		if (y < -0.2) y = -0.2;
		else if (y > 1) y = 1;
		setHard(core, i, core.relX[i] + core.vx[i] * dt, y, frame);

		//Gravity + velocity clamps:
		core.vy[i] += core.gravity[i] * dt;

		if (core.vy[i] > 0.006) {
			core.vy[i] = 0.006;
		}

		if (core.vy[i] < -0.006) {
			core.vy[i] = -0.006;
		}

		if (core.vx[i] > 0.0015) {
			core.vx[i] = 0.0015;
		}

		if (core.vx[i] < -0.0015) {
			core.vx[i] = -0.0015;
		}

		//_processBoundary — ALWAYS the box (see header); cords come from the
		//depth behaviour's original cords when one is attached:
		if (phys[i] & PHYS_BOUNDARY) {
			const cordsX = mask[i] & BEHAVIOUR_DEPTH ? core.origX[i] : core.relX[i];
			const cordsY = mask[i] & BEHAVIOUR_DEPTH ? core.origY[i] : core.relY[i];

			if (cordsX > boundaryX + frame.boundaryW && core.vx[i] > 0) {
				core.vx[i] *= -1;
			} else if (cordsX < boundaryX && core.vx[i] < 0) {
				core.vx[i] *= -1;
			}

			if (cordsY > frame.boundaryY + frame.boundaryH && core.vy[i] > 0) {
				if (phys[i] & PHYS_RAIN) {
					raindrop(core, i, frame);
				} else if (phys[i] & PHYS_Y_BOUNCE) {
					core.vy[i] *= -1;
				} else {
					core.vy[i] = 0;
					core.gravity[i] = 0;
				}
			} else if (cordsY < frame.boundaryY && core.vy[i] < 0) {
				core.vy[i] *= -1;
			}
		}
	}
}
